import { Slot } from "@radix-ui/react-slot";
import type { ComponentProps } from "react";
import { tv, type VariantProps } from "tailwind-variants";

const buttonVariants = tv({
  base: [
    "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap",
    "text-sm font-medium",
    "rounded-md outline-none",
    "cursor-pointer transition-all active:scale-98",
    "disabled:pointer-events-none disabled:opacity-50",
    "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
    "aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  ],
  variants: {
    variant: {
      default:
        "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
      destructive: [
        "bg-destructive text-white shadow-xs hover:bg-destructive/90",
        "focus-visible:ring-destructive/20",
        "dark:bg-destructive/60 dark:focus-visible:ring-destructive/40",
      ],
      outline: [
        "border bg-transparent shadow-xs hover:bg-muted",
        "dark:border dark:bg-input/30 dark:hover:bg-input/50",
      ],
      secondary:
        "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
      ghost: "hover:bg-muted dark:hover:bg-accent",
      link: "text-primary underline-offset-4 hover:underline",
    },
    size: {
      default: "h-9 px-4 py-2 has-[>svg]:px-3",
      xs: "h-7 gap-1 rounded-md px-2 text-xs has-[>svg]:px-2",
      sm: "h-8 gap-1.5 rounded-md px-3 text-xs has-[>svg]:px-2.5",
      lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
      icon: "size-9",
      "icon-xs": [
        "size-7 rounded-[min(var(--radius-md),10px)]",
        "in-data-[slot=button-group]:rounded-lg",
        "[&_svg:not([class*='size-'])]:size-3",
      ],
      "icon-sm": [
        "size-8 rounded-[min(var(--radius-md),12px)]",
        "in-data-[slot=button-group]:rounded-lg",
      ],
      "icon-md": [
        "size-9 rounded-[min(var(--radius-md),12px)]",
        "in-data-[slot=button-group]:rounded-lg",
      ],
      "icon-lg": "size-10",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

export type ButtonProps = ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & { asChild?: boolean };

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={buttonVariants({ variant, size, className })}
      {...props}
    />
  );
}

export { Button, buttonVariants };
